import type { AdminMenuIconName } from '../../shared/admin';
import type { AdminMenuItem } from '../layout/admin-sidebar.component';

/** Sidebar menu keyed by moduleKey — source for Module Permissions. */
export type PlatformMenuLink = {
  label: string;
  route: string;
  moduleKey: string;
};

export type PlatformMenuGroup = {
  label: string;
  icon: AdminMenuIconName;
  groupKey: string;
  defaultExpanded?: boolean;
  children: PlatformMenuLink[];
};

export type PlatformMenuSection = { section: string };

export type PlatformMenuConfigItem = PlatformMenuLink | PlatformMenuGroup | PlatformMenuSection;

export function isPlatformMenuGroup(item: PlatformMenuConfigItem): item is PlatformMenuGroup {
  return 'children' in item;
}

export function isPlatformMenuSection(item: PlatformMenuConfigItem): item is PlatformMenuSection {
  return 'section' in item;
}

export const PLATFORM_SIDEBAR_MENU: PlatformMenuConfigItem[] = [
  { label: 'Dashboard', route: '/platform/dashboard', moduleKey: 'dashboard' },
  { label: 'Franchises', route: '/platform/franchises', moduleKey: 'franchises' },
  {
    label: 'Website Content',
    icon: 'layers',
    groupKey: 'website',
    defaultExpanded: false,
    children: [
      { label: 'Home Page', route: '/platform/marketing/home', moduleKey: 'website.home' },
      { label: 'Success Stories', route: '/platform/success-stories', moduleKey: 'website.successStories' },
      { label: 'Testimonials', route: '/platform/testimonials', moduleKey: 'website.testimonials' },
      { label: 'Countries', route: '/platform/countries', moduleKey: 'website.countries' },
      { label: 'Universities', route: '/platform/universities', moduleKey: 'website.universities' },
      { label: 'Blogs', route: '/platform/blogs', moduleKey: 'website.blogs' },
      { label: 'Media', route: '/platform/media', moduleKey: 'website.media' },
    ],
  },
  { section: 'OPERATIONS' },
  { label: 'Leads', route: '/platform/leads', moduleKey: 'leads' },
  { label: 'Enrolled Applicants', route: '/platform/applicants', moduleKey: 'applicants' },
  { label: 'Feature Requests', route: '/platform/feature-requests', moduleKey: 'featureRequests' },
  {
    label: 'CRM',
    icon: 'briefcase',
    groupKey: 'crm',
    defaultExpanded: false,
    children: [
      { label: 'Lead Statuses', route: '/platform/crm/lead-statuses', moduleKey: 'crm.leadStatuses' },
      {
        label: 'Applicant Statuses',
        route: '/platform/crm/applicant-statuses',
        moduleKey: 'crm.applicantStatuses',
      },
    ],
  },
  {
    label: 'Staff Management',
    icon: 'users',
    groupKey: 'staff',
    children: [
      { label: 'Platform Users', route: '/platform/users', moduleKey: 'staff.platformUsers' },
      {
        label: 'Roles & Permissions',
        route: '/platform/roles-permissions',
        moduleKey: 'staff.rolePermissions',
      },
    ],
  },
  {
    label: 'Settings',
    icon: 'settings',
    groupKey: 'settings',
    defaultExpanded: false,
    children: [
      { label: 'General', route: '/platform/settings', moduleKey: 'settings.general' },
      {
        label: 'Release Notes',
        route: '/platform/settings/release-notes',
        moduleKey: 'settings.releaseNotes',
      },
    ],
  },
];

export function toAdminMenuItems(items: PlatformMenuConfigItem[]): AdminMenuItem[] {
  return items.map((item) => {
    if (isPlatformMenuSection(item)) return { section: item.section };
    if (isPlatformMenuGroup(item)) {
      return {
        label: item.label,
        icon: item.icon,
        defaultExpanded: item.defaultExpanded,
        children: item.children.map((c) => ({ label: c.label, route: c.route })),
      };
    }
    return { label: item.label, route: item.route };
  });
}
